import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { DocumentTypes } from 'constants/documents';
import { EXTERNAL_SHARE_EXCHANGE_PAGE_TITLE } from 'constants/external-share-exchange';
import { LocalStorageKeys } from 'constants/global';
import { ConstraintVariants } from 'constants/shared';
import { UserTypes } from 'constants/user';
import useFilterParameters from 'hooks/use-filter-parameters/useFilterParameters';
import { usePaginationWithSearch } from 'hooks/use-pagination-with-search/usePaginationWithSearch';
import useTableSorting from 'hooks/use-table-sorting/useTableSorting';
import { selectIsAdmin } from 'modules/current-user/selectors';
import { getDocuments } from 'modules/documents/action';
import { selectDocumentsPaginationData } from 'modules/documents/selectors';
import { getExternalShareExchange } from 'modules/external-share-exchange/action';
import { selectExternalShareExchange } from 'modules/external-share-exchange/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { DashboardLayout } from 'page-components';
import LocalDocumentsTable from 'page-components/documents-page/LocalDocumentsTable';
import ShareExchangeRequestTransactionSummary from 'page-components/my-investments/start-investment-share-exchange-page/ShareExchangeRequestTransactionSummary';
import { Constraint, FullScreenLoader, Pagination, TitleWithDescription } from 'shared-components';
import { getUserFiltersQuery } from 'utils';

const DOCUMENTS_PER_PAGE = 7;

const ExternalShareExchange = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const documentsPaginationData = useAppSelector(selectDocumentsPaginationData);
  const data = useAppSelector(selectExternalShareExchange);
  const { sorting, updateSorting } = useTableSorting();
  const userType = localStorage.getItem(LocalStorageKeys.USER_TYPE) as UserTypes;
  const { page, setPage, query } = usePaginationWithSearch();
  const { filter } = useFilterParameters();
  const isAdmin = useAppSelector(selectIsAdmin);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      dispatch(getExternalShareExchange({ id, isAdmin }))
        .unwrap()
        .then(() => setIsLoading(false))
        .catch(() => {
          setIsLoading(false);
          navigate('/');
        });
    }
  }, [dispatch, id, isAdmin, navigate]);

  useEffect(() => {
    dispatch(
      getDocuments({
        page,
        per_page: DOCUMENTS_PER_PAGE,
        userType,
        order: sorting.order,
        sort: sorting.column,
        ...(query ? { query } : {}),
        filter: `${filter || ''}&${getUserFiltersQuery({
          documentType: DocumentTypes.OFFPLATFORM_SHARE_EXCHANGE,
          external_deal_id: id,
        })}`.replace(/&$/, ''),
      }),
    );
  }, [dispatch, filter, page, query, userType, id, sorting.order, sorting.column]);

  if (isLoading) return <FullScreenLoader withoutSidebar hideOverlay />;

  return (
    <DashboardLayout>
      <Constraint variant={ConstraintVariants.FULL_ROUNDED}>
        <TitleWithDescription descriptionClassName='pt-2' title={EXTERNAL_SHARE_EXCHANGE_PAGE_TITLE} />
        <div className='mt-6 mb-8 3xl:mt-4'>
          <ShareExchangeRequestTransactionSummary
            dealExchanges={data ? [data] : []}
            companyName={data?.companyName}
            isExternalDeal={true}
          />
        </div>
        <LocalDocumentsTable itemsPerPage={7} updateSorting={updateSorting}>
          <Pagination currentPage={page} updatePage={setPage} {...documentsPaginationData} />
        </LocalDocumentsTable>
      </Constraint>
    </DashboardLayout>
  );
};

export default ExternalShareExchange;
